import React from "react";
import cn from "./helpers/cn";
import { useFormControl, } from "./FormControl";

type Variant = "standard" | "outlined" | "filled";
type Margin = "dense" | "normal" | "none";
type Color = "primary" | "secondary";

interface InputLabelProps extends Omit<React.LabelHTMLAttributes<HTMLLabelElement>, "color"> {
  shrink?: boolean;
  disableAnimation?: boolean;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  focused?: boolean;
  variant?: Variant;
  margin?: Margin;
  color?: Color;
  children?: React.ReactNode;
}

// Transformações por (variant, shrink, margin). Reproduzem o MUI v4 InputLabel.
function getTransform(variant: Variant, shrink: boolean, margin: Margin) {
  if (variant === "outlined") {
    if (shrink) return "translate-x-[14px] -translate-y-1.5 scale-75";
    if (margin === "dense") return "translate-x-[14px] translate-y-3 scale-100";

    return "translate-x-[14px] translate-y-5 scale-100";
  }
  if (variant === "filled") {
    if (shrink) {
      return margin === "dense"
        ? "translate-x-3 translate-y-[7px] scale-75"
        : "translate-x-3 translate-y-[10px] scale-75";
    }
    if (margin === "dense") return "translate-x-3 translate-y-[17px] scale-100";

    return "translate-x-3 translate-y-5 scale-100";
  }

  // standard
  if (shrink) return "translate-y-[1.5px] scale-75";
  if (margin === "dense") return "translate-y-[21px] scale-100";

  return "translate-y-6 scale-100";
}

const InputLabel = React.forwardRef<HTMLLabelElement, InputLabelProps>((props, ref) => {
  const {
    className,
    shrink: shrinkProp,
    disableAnimation = false,
    required: requiredProp,
    disabled: disabledProp,
    error: errorProp,
    focused: focusedProp,
    variant: variantProp,
    margin: marginProp,
    color: colorProp,
    children,
    ...other
  } = props;

  // Fallback para FormControlContext (mesma cadeia de prioridade do MUI v4).
  const fc = useFormControl();

  const variant: Variant = variantProp ?? fc?.variant ?? "standard";
  const margin: Margin = marginProp ?? fc?.margin ?? "none";
  const color: Color = colorProp ?? fc?.color ?? "primary";
  const required = requiredProp ?? fc?.required ?? false;
  const disabled = disabledProp ?? fc?.disabled ?? false;
  const error = errorProp ?? fc?.error ?? false;
  const focused = focusedProp ?? fc?.focused ?? false;
  // Quando não definido, label encolhe quando o FormControl está focado ou tem valor.
  // Como não rastreamos "tem valor" no contexto, segue só `focused` por padrão.
  const shrink = shrinkProp ?? focused ?? false;

  const colorClass =
    error ? "text-red-600"
      : disabled ? "text-slate-400"
        : focused ? (color === "primary" ? "text-primary" : "text-secondary")
          : "text-slate-500";

  return (
    <label
      ref={ref}
      data-shrink={shrink || undefined}
      className={cn(
        "",
        // Variantes outlined/filled ficam acima do input (z) sem interceptar clique.
        (variant === "outlined" || variant === "filled") && "z-10",
        // Animação MUI: transition em color e transform.
        !disableAnimation && "transition-[color,transform] duration-200 ease-out",
        getTransform(variant, shrink, margin),
        colorClass,
        className
      )}
      {...other}
    >
      {children}
      {required && <span aria-hidden="true" className="ml-0.5 text-red-500">*</span>}
    </label>
  );
});

InputLabel.displayName = "InputLabel";

export default InputLabel;
